import { useCallback, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import BancontactAdyenCreditCardForm, {
  BancontactAdyenCreditCardFormRef,
} from '@components/BancontactAdyenCreditCardForm/BancontactAdyenCreditCardForm';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import CoreProviders, { CoreProvidersProps } from '@providers/CoreProviders';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { CustomerType } from '@types-internal';
import { AdyenEnvironment } from '@types-internal/AdyenTypes';

export interface BancontactAdyenCaptureCreditCardsProps extends CoreProvidersProps {
  customerType: CustomerType;
  currency: string;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
}

interface InnerBancontactAdyenCaptureCreditCardsProps {
  customerType: CustomerType;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
}

/**
 * Component to solely capture the credit card information with Adyen.
 * Does not provide a mean to save it: external component must use the globally-exposed 'computeBancontactAdyenCreditCardFormData'.
 *
 * To be used on the registration page.
 */
const InnerBancontactAdyenCaptureCreditCards: React.FC<
  InnerBancontactAdyenCaptureCreditCardsProps
> = ({ customerType, isFallbackPsp, adyenEnvironment }) => {
  const form = useRef<BancontactAdyenCreditCardFormRef>(null);
  const { config } = useConfig();
  const { t } = useTranslation();
  const { setError: setBancontactError } = useErrorContext();
  const [showForm, setShowForm] = useState(true);

  const onError = useCallback(
    (error: unknown, showFormArg?: boolean) => {
      captureException({ error });
      setBancontactError({
        message: t('checkout.payment.creditcard.error.addcard.apicall'),
      });
      setShowForm(typeof showFormArg === 'undefined' ? true : showFormArg);
    },
    [setBancontactError],
  );

  return showForm ? (
    <>
      <Alert>
        <Trans
          t={t}
          i18nKey="checkout.payment.creditcard.3d.secure.message"
          components={{ url: <a target="_blank" href="/info/about/psd2_summary" /> }}
        />
      </Alert>
      <BancontactAdyenCreditCardForm
        ref={form}
        adyenEnvironment={adyenEnvironment}
        clientKey={config.adyen.clientKey}
        onFatalError={onError}
        allowVaulting={!isFallbackPsp && customerType !== 'GUEST_CUSTOMER'}
      />
    </>
  ) : null;
};

/*
 * Wrapper over the true component, because we first need to call the CoreProviders.
 */
const BancontactAdyenCaptureCreditCards: React.FC<BancontactAdyenCaptureCreditCardsProps> = ({
  env,
  shopBundle,
  locale,
  domain,
  errorContainerId,
  csrfToken,
  customerType,
  isFallbackPsp,
  adyenEnvironment,
}) => {
  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      csrfToken={csrfToken}
      featureName="bancontact-adyen-credit-card">
      <InnerBancontactAdyenCaptureCreditCards
        adyenEnvironment={adyenEnvironment}
        isFallbackPsp={isFallbackPsp}
        customerType={customerType}
      />
    </CoreProviders>
  );
};

export default BancontactAdyenCaptureCreditCards;
